import { Button } from '@/components/ui/button'
import { ArrowRight } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import onboardingImage from '@/assets/images/onboarding 1.png'

type WelcomeStepProps = {
  onNext: () => void
  onSkip: () => void
}

export function WelcomeStep({ onNext, onSkip }: WelcomeStepProps) {
  const { t } = useTranslation('onboarding')
  const { user } = useAuth()

  const firstName = user?.name?.split(' ')[0] || ''

  return (
    <div className="flex flex-col items-center text-center px-4 py-8 animate-in fade-in duration-500">
      {/* Illustration - Using imported image */}
      <div className="relative w-full max-w-md mb-8">
        <img 
          src={onboardingImage}
          alt="Welcome illustration"
          className="w-full h-auto rounded-2xl shadow-lg"
        />
      </div>

      {/* Welcome text */}
      <div className="space-y-4 max-w-lg">
        <h2 className="text-3xl font-bold text-[#0B2A4A] dark:text-white leading-tight">
          {firstName 
            ? t('welcome.titleWithName', { name: firstName })
            : t('welcome.title')
          }
        </h2>
        
        <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
          {t('welcome.subtitle')}
        </p>

        <p className="text-gray-500 dark:text-gray-400">
          {t('welcome.description')}
        </p>
      </div>

      {/* Features preview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 w-full max-w-lg">
        {[
          { icon: '📅', text: t('welcome.features.calendar') },
          { icon: '💳', text: t('welcome.features.payments') },
          { icon: '📝', text: t('welcome.features.contracts') },
        ].map((feature, index) => (
          <div
            key={index}
            className={cn(
              'flex items-center gap-3 p-3 rounded-lg',
              'bg-gray-50 dark:bg-gray-800/50',
              'animate-in fade-in slide-in-from-bottom-2',
              'duration-300'
            )}
            style={{ animationDelay: `${(index + 1) * 100}ms` }}
          >
            <span className="text-2xl">{feature.icon}</span>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {feature.text}
            </span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center gap-3 mt-10 w-full max-w-sm">
        <Button
          onClick={onNext}
          size="lg"
          className={cn(
            'w-full sm:flex-1 h-12',
            'bg-[#F08A24] hover:bg-[#e07a14] text-white',
            'shadow-lg shadow-[#F08A24]/25',
            'transition-all duration-300 hover:scale-[1.02]'
          )}
        >
          {t('welcome.cta')}
          <ArrowRight weight="bold" className="ml-2 w-5 h-5" />
        </Button>
        
        <Button
          onClick={onSkip}
          variant="ghost"
          size="lg"
          className="w-full sm:w-auto text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          {t('skipSetup')}
        </Button>
      </div>
    </div>
  )
}
